import React, { createContext, useContext, useEffect, useState } from 'react';

interface ISimpleTabsContext {
	selectedCycleId: number | null;
	setSelectedCycleId: (cycleId: number | null, context?: string) => void;
	getCycleForContext: (context: string, fallback?: number) => number | null;
	clearContextCycle: (context: string) => void;
}

const SimpleTabsContext = createContext<ISimpleTabsContext | undefined>(
	undefined
);

const STORAGE_KEY_PREFIX = 'easy360_selected_cycle';
const DEFAULT_STORAGE_KEY = 'easy360_selected_cycle_id';

const getStorageKey = (context?: string) => {
	return context ? `${STORAGE_KEY_PREFIX}_${context}` : DEFAULT_STORAGE_KEY;
};

export const SimpleTabsProvider: React.FC<{ children: React.ReactNode }> = ({
	children,
}) => {
	const [selectedCycleId, setSelectedCycleIdState] = useState<number | null>(
		() => {
			const stored = sessionStorage.getItem(DEFAULT_STORAGE_KEY);
			return stored ? parseInt(stored, 10) : null;
		}
	);

	const setSelectedCycleId = (cycleId: number | null, context?: string) => {
		const storageKey = getStorageKey(context);

		if (!context) {
			setSelectedCycleIdState(cycleId);
		}

		if (cycleId !== null) {
			sessionStorage.setItem(storageKey, cycleId.toString());
		} else {
			sessionStorage.removeItem(storageKey);
		}
	};

	const getCycleForContext = (
		context: string,
		fallback?: number
	): number | null => {
		const contextKey = getStorageKey(context);
		const contextStored = sessionStorage.getItem(contextKey);

		if (contextStored) {
			return parseInt(contextStored, 10);
		}

		return fallback || selectedCycleId;
	};

	const clearContextCycle = (context: string) => {
		const storageKey = getStorageKey(context);
		sessionStorage.removeItem(storageKey);
	};

	useEffect(() => {
		if (performance.navigation?.type === 1) {
			Object.keys(sessionStorage)
				.filter(key => key.startsWith(STORAGE_KEY_PREFIX))
				.forEach(key => sessionStorage.removeItem(key));
			setSelectedCycleIdState(null);
		}
	}, []);

	useEffect(() => {
		const handleStorageChange = () => {
			const stored = sessionStorage.getItem(DEFAULT_STORAGE_KEY);
			const newValue = stored ? parseInt(stored, 10) : null;
			if (newValue !== null) {
				setSelectedCycleIdState(newValue);
			}
		};

		window.addEventListener('storage', handleStorageChange);
		return () => window.removeEventListener('storage', handleStorageChange);
	}, []);

	return (
		<SimpleTabsContext.Provider
			value={{
				selectedCycleId,
				setSelectedCycleId,
				getCycleForContext,
				clearContextCycle,
			}}
		>
			{children}
		</SimpleTabsContext.Provider>
	);
};

export const useSimpleTabs = (): ISimpleTabsContext => {
	const context = useContext(SimpleTabsContext);
	if (context === undefined) {
		throw new Error(
			'useSimpleTabs must be used within a SimpleTabsProvider'
		);
	}
	return context;
};
